import { Component, JSX } from "react";
import { AdequacyAggregate } from "@app/projects/network-assessment/adequacy";
import { FilterItemType } from "@app/resource-filters/common";
import { FilterItem } from "@app/resource-filters/filter-items";
import { FilteredResource, ResourceStatus } from "@app/resources";
import { ColumnConfig, numberSort, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { FlyoutContextOutgoingProps, withFlyoutContext } from "@app/shared/Flyouts";
import ProjectService from "@app/projects/projectService";
import { QESButton } from "@app/shared/qes-standards/Button";

type SpecialtyTableProps = FlyoutContextOutgoingProps & {
  filteredResources: FilteredResource[];
  handleAddFilter: (newFilterItem: FilterItem) => void;
  showingArchived: boolean;
}

interface SpecialtyTableState {
  loading: boolean;
  sortedRecords: SpecialtyTableData[];
  specialtyGroupAggregates: AdequacyAggregate[];
}

interface SpecialtyTableData {
  [wafflesKeyColumnName]: string;
  specialty: JSX.Element;
  numberOfGaps: string;
  numberOfProjects: string;
  percentageOfGaps: string;
}

class WrappedSpecialtyTable extends Component<SpecialtyTableProps, SpecialtyTableState> {
  private readonly projectService = new ProjectService();

  private columnConfig: ColumnConfig = {
    numberOfGaps: {
      displayText: "Gaps #",
      testId: "number-of-gaps",
    },
    numberOfProjects: {
      displayText: "Projects #",
      testId: "number-of-projects",
    },
    percentageOfGaps: {
      displayText: "Gaps %",
      testId: "percentage-of-gaps",
    },
    specialty: {
      displayText: "Specialty Group",
      testId: "specialty-group",
    },
  };

  constructor(props: SpecialtyTableProps) {
    super(props);

    this.state = {
      loading: true,
      sortedRecords: [],
      specialtyGroupAggregates: null,
    };
  }

  public async componentDidMount() {
    const specialtyGroupAggregates = await this.projectService.getSpecialtyGroupAggregates(
      this.props.filteredResources,
      this.props.showingArchived ? ResourceStatus.Archived : ResourceStatus.Active);

    const newSpecialtyData = specialtyGroupAggregates.map((row) => ({
      filterUrl: `\u00A7Specialties\u00A7${row.specialtyGroupDisplayName} (${row.specialtyGroupCode})`,
      key: `${row.specialtyGroupDisplayName} (${row.specialtyGroupCode})`,
      numberOfGaps: row.gapCount,
      numberOfProjects: row.projectCount,
      percentageOfGaps: row.percentGaps * 100,
    }));

    newSpecialtyData.sort((a, b) => {
      if (a.percentageOfGaps === b.percentageOfGaps) {
        if (a.key > b.key) {
          return 1;
        } else {
          return -1;
        }
      } else if (a.percentageOfGaps < b.percentageOfGaps) {
        return 1;
      } else {
        return -1;
      }
    });

    //don't render the specialty group code as a link if there's already a specialty group filter
    const rowClickable =
      this.props.filteredResources &&
      !this.props.filteredResources.some((fr) => fr.specialtyGroupCodes && fr.specialtyGroupCodes.length > 0);

    /* oxlint-disable sort-keys */
    const tableData: SpecialtyTableData[] = newSpecialtyData.map((data) => ({
      [wafflesKeyColumnName]: data.key,
      specialty: rowClickable ? <QESButton qesType="secondary" qesTestId={`region-table-add-filter--${data.key}`} key={data.key} onClick={() => this.handleAddSpecialtyGroupFilter(data.filterUrl)}>{data.key}</QESButton> : <span key={data.key}>{data.key}</span>,
      numberOfProjects: data.numberOfProjects.toLocaleString("en-US"),
      numberOfGaps: data.numberOfGaps.toLocaleString("en-US"),
      percentageOfGaps: isNaN(data.percentageOfGaps) ? "---" : `${data.percentageOfGaps.toFixed(1)}%`,
    }));
    /* oxlint-enable sort-keys */

    this.setState({
      loading: false,
      sortedRecords: tableData,
      specialtyGroupAggregates,
    });
  }

  public render() {

    return (
      <>
        <Waffles<SpecialtyTableData>
          columns={this.columnConfig}
          customSort={this.customSort}
          data={this.state.sortedRecords}
          loading={this.state.loading}
          testId="dashboard-specialty-table"
        />
      </>
    );
  }

  private handleAddSpecialtyGroupFilter = (filterUrl: string) => {
    const newFilterItem: FilterItem = {
      displayName: "Item",
      itemType: FilterItemType.Leaf,
      itemValue: "Value",
      namespacePath: filterUrl,
    };
    this.props.handleCloseFlyout().then(() => {
      this.props.handleAddFilter(newFilterItem);
    });
  }

  private customSort = (data: SpecialtyTableData[], columnKey: keyof SpecialtyTableData, sortDirection: SortDirections) => {
    const multiplier = sortDirection === SortDirections.Asc ? 1 : -1;

    switch (columnKey) {
      case "specialty":
        return data.sort((a, b) => (a[columnKey].key > b[columnKey].key ? 1 : -1) * multiplier);
      case "numberOfGaps":
      case "numberOfProjects":
      case "percentageOfGaps":
        return numberSort(data, columnKey, multiplier);
    }
  }
}

const SpecialtyTable = withFlyoutContext(WrappedSpecialtyTable);
export default SpecialtyTable;
